import { motion } from '@/lib/motion';
import { BookOpen, HeadphonesIcon, MessageSquare, Target, Users } from 'lucide-react';

const AGENT_ROLES = [
  {
    icon: Users,
    title: 'Front desk',
    body: 'Welcomes every caller, notes why they rang, and promises a callback. The default for any business.',
  },
  {
    icon: BookOpen,
    title: 'Appointment booker',
    body: 'Captures name, contact, and preferred time so your team can confirm the slot.',
  },
  {
    icon: Target,
    title: 'Lead qualifier',
    body: 'Gathers budget, timeline, and intent so a specialist can follow up prepared.',
  },
  {
    icon: HeadphonesIcon,
    title: 'Support desk',
    body: 'Triages issues — billing, login, refunds — captures details, and alerts your team by email.',
    highlight: true,
  },
  {
    icon: MessageSquare,
    title: 'And more…',
    body: 'FAQ handler, urgency router, reservation taker, order taker. One agent, one job — perfectly focused.',
  },
];

export function AgentRolesSection(): JSX.Element {
  return (
    <section className="border-y border-border bg-muted/20 py-14 sm:py-20">
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-[var(--brand-accent)]">
            Specialized by design
          </span>
          <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
            Many agents. One knowledge base.
          </h2>
          <p className="mt-4 text-muted-foreground">
            Each agent does exactly one job — trained from the same knowledge base
            you already built. Add a support agent, a booking agent, or a lead
            qualifier without duplicating anything.
          </p>
        </div>

        <div className="mx-auto mt-14 grid max-w-5xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {AGENT_ROLES.map(({ icon: Icon, title, body, highlight }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className={`rounded-xl border p-6 shadow-sm ${
                highlight
                  ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)]/5 shadow-[var(--brand-accent)]/10'
                  : 'border-border bg-card'
              }`}
            >
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                  highlight
                    ? 'bg-[var(--brand-accent)] text-white'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-semibold">{title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{body}</p>
            </motion.div>
          ))}
        </div>

        <p className="mt-10 text-center text-sm text-muted-foreground">
          All agents share one knowledge base — train once, deploy as many focused agents as you need.
        </p>
      </div>
    </section>
  );
}
